/* eslint-disable no-nested-ternary */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { Button } from "@mui/material";
import { Tooltip, useFinanceableTheme } from "@financeable-com-au/financeable-ui";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import AssetValue from "./QuoteComponents/AssetValue";
import Deposit from "./QuoteComponents/Deposit";
import AssetType from "./QuoteComponents/AssetType";
import TypeOfSale from "./QuoteComponents/TypeOfSale";
import AgeOfAsset from "./QuoteComponents/AgeOfAsset";
import Term from "./QuoteComponents/Term";
import Balloon from "./QuoteComponents/Balloon";
import QuoteEntity from "./QuoteComponents/QuoteEntity";
import LengthOfEmployment from "./QuoteComponents/LengthOfEmployment";
import EmploymentType from "./QuoteComponents/EmploymentType";
import QuoteCreditScore from "./QuoteComponents/QuoteCreditScore";
import LivingArrangements from "./QuoteComponents/LivingArrangements";
import QuoteDateOfBirth from "./QuoteComponents/QuoteDateOfBirth";
import AssetCondition from "./QuoteComponents/AssetCondition";
import Brokerage from "./QuoteComponents/Brokerage";
import OriginationFee from "./QuoteComponents/OriginationFee";
import RateAdjustment from "./QuoteComponents/RateAdjustment";
import { RequestedLoanAmount } from "./QuoteComponents/RequestedLoanAmount";
import { LoanPurpose } from "./QuoteComponents/LoanPurpose";
import Email from "../CustomerDetails/Email";
import {
  ERRORS_MESSAGE,
  SUCCESS_MESSAGE,
  TOOLTIP_MESSAGES,
  getIsAssetLoan,
  getIsConsumerOrPersonalApplication,
  getIsCommercialOrBusinessApplication,
} from "../../constants";
import {
  applicationSelector,
  clearProductsQuotes,
  clearState,
  getApplication,
  saveValue,
  updateAppStatusToWorkShop,
  userDetailsSelector,
} from "../../store/slices/applicationFormSlice";
import { resetState, userPermissionsSelector } from "../../store/slices/userSlice";
import PageLoader from "../Application/PageLoader";
import ProductSelector from "../ProductSelector";
import {
  useGetCommercialLendersQuery,
  useGetBusinessLendersQuery,
  useGetConsumerLendersQuery,
  useGetPersonalLendersQuery,
} from "../../store/slices/apiSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import { sendQuoteToEmail } from "../../store/slices/quoteSlice";
import { Loader } from "../Loader";

const formFieldsStyle = {
  margin: "0 0 14px",
};

const QuoteCondensed = () => {
  const theme = useFinanceableTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = useSelector(userPermissionsSelector);
  const isQuoteEdit = permissions?.application?.quote_edit;
  const disclaimer = localStorage.disclaimer;
  const { applicationId } = useParams();
  const { gettingQuote, startApplicationBtnLoader } = useSelector(userDetailsSelector);
  const application = useSelector(applicationSelector);

  const [fieldErrors, setFieldErrors] = useState({
    assetValue: "",
  });

  const [quoteValues, setQuoteValue] = useState({
    assetValue: 0,
    deposit: 0,
  });

  const [sendQuoteLoader, setSendQuoteLoader] = useState(false);

  async function handleApplicationLoad() {
    dispatch(saveValue({ gettingQuote: true, isQuoteOpen: true, open: true }));
    if (applicationId) await dispatch(getApplication(applicationId)).unwrap();
  }

  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(application.applicationType);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(application.applicationType);
  const isAssetLoan = getIsAssetLoan(application.applicationType);

  const [skipConsumer, setSkipConsumer] = useState(true);
  const [skipPersonal, setSkipPersonal] = useState(true);
  const [skipCommercial, setSkipCommercial] = useState(true);
  const [skipBusiness, setSkipBusiness] = useState(true);

  const consumerResult = useGetConsumerLendersQuery({}, { skip: skipConsumer });
  const personalResult = useGetPersonalLendersQuery({}, { skip: skipPersonal });
  const commercialResult = useGetCommercialLendersQuery({}, { skip: skipCommercial });
  const businessResult = useGetBusinessLendersQuery({}, { skip: skipBusiness });

  useEffect(() => {
    switch (application.applicationType) {
      case ApplicationType.Consumer:
        setSkipConsumer(false);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
      case ApplicationType.Personal:
        setSkipConsumer(true);
        setSkipPersonal(false);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
      case ApplicationType.Commercial:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(false);
        setSkipBusiness(true);
        break;
      case ApplicationType.Business:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(false);
        break;
      default:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
    }
  }, [application.applicationType]);

  // Determine which result to use based on applicationType
  const result =
    application.applicationType === ApplicationType.Consumer
      ? consumerResult
      : application.applicationType === ApplicationType.Personal
        ? personalResult
        : application.applicationType === ApplicationType.Commercial
          ? commercialResult
          : application.applicationType === ApplicationType.Business
            ? businessResult
            : { data: null, error: null, isLoading: false };

  const { data: lenderData } = result;

  useEffect(() => {
    handleApplicationLoad();

    return () => {
      dispatch(clearState());
    };
  }, [applicationId]);

  const calculatorRef = useRef(null);

  useEffect(() => {
    const inactivityTimer = setTimeout(() => {
      localStorage.clear();
      dispatch(resetState());
    }, 3600000);
    return () => {
      clearTimeout(inactivityTimer);
    };
  }, [application]);

  const isConsumer = application.applicationType === ApplicationType.Consumer;

  const customer = application?.customers?.[0];
  const findEmail = customer?.emails?.find((email) => email.status === "current");

  const isCustomerEmailMissing = !findEmail?.address;
  const selectedQuotesCount = application?.productsQuotes?.length || 0;
  const hasSelectedQuotes = selectedQuotesCount > 0;

  const isSendQuoteDisabled = !isQuoteEdit || isCustomerEmailMissing || !hasSelectedQuotes || sendQuoteLoader;

  // Show the most relevant reason the Send Quote button is unavailable.
  const sendQuoteTooltip = (() => {
    if (isCustomerEmailMissing) return TOOLTIP_MESSAGES.SEND_QUOTE_ADD_CUSTOMER_EMAIL;
    if (!hasSelectedQuotes) return TOOLTIP_MESSAGES.SEND_QUOTES_SELECT_QUOTE_LIMIT;
    return "";
  })();

  const quoteTitle = `${application?.applicationType?.charAt(0).toUpperCase() + application?.applicationType?.slice(1)} quote - ${application.humanId}`;

  useEffect(() => {
    localStorage.setItem("disclaimer", true);
  }, [disclaimer]);

  const handle = {
    sendQuoteHandler: () => {
      setSendQuoteLoader(true);
      dispatch(sendQuoteToEmail({ applicationId })).then((res) => {
        setSendQuoteLoader(false);
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(clearProductsQuotes());
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.productQuoteSentToCustomers,
              intent: "success",
            }),
          );
        }
      });
    },
    startApplicationHandler: () => {
      const updateStatusPayload = {
        applicationId,
        customerId: application.customers[0]?._id,
      };

      dispatch(updateAppStatusToWorkShop(updateStatusPayload)).then((res) => {
        if (res?.payload?.data?.data) {
          navigate(`/application/${applicationId}`);
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      });
    },
  };

  return (
    <Grid container item>
      {gettingQuote ? (
        <Grid
          container
          item
          style={{
            justifyContent: "center",
          }}
        >
          <Loader text="Quote loading..." />
        </Grid>
      ) : (
        <>
          <Grid
            container
            item
            sm={4}
            md={3}
            lg={3}
            ref={calculatorRef}
            style={{
              padding: "6px 0",
            }}
          >
            <Box
              sx={{
                overflow: "hidden auto",
                height: "calc(100vh - 105px)",
                position: "fixed",
                width: `${calculatorRef?.current?.offsetWidth}px`,
                paddingBottom: "50px",
              }}
            >
              <Grid style={{ maxWidth: `${calculatorRef?.current?.offsetWidth}px`, padding: "0 20px" }}>
                <Typography variant="h6" style={{ marginBottom: "15px" }}>
                  {quoteTitle}
                </Typography>
                {!isAssetLoan && (
                  <>
                    <Grid item md={12} sm={12} xs={12} style={{ margin: "0 0 14px" }}>
                      <RequestedLoanAmount
                        applicationId={applicationId}
                        fieldErrors={fieldErrors}
                        setFieldErrors={setFieldErrors}
                        quoteValues={quoteValues}
                        setQuoteValue={setQuoteValue}
                        disabled={!isQuoteEdit}
                      />
                    </Grid>
                    <Grid item md={12} sm={12} xs={12} style={{ margin: "0 0 14px" }}>
                      <LoanPurpose
                        applicationId={applicationId}
                        quoteValues={quoteValues}
                        setQuoteValue={setQuoteValue}
                        disabled={!isQuoteEdit}
                      />
                    </Grid>
                  </>
                )}
                {isAssetLoan && (
                  <>
                    <Grid container item direction="row" spacing={1}>
                      <Grid item lg={6} md={12} sm={12} xs={12} style={{ margin: "0 0 14px" }}>
                        <AssetValue
                          applicationId={applicationId}
                          fieldErrors={fieldErrors}
                          setFieldErrors={setFieldErrors}
                          quoteValues={quoteValues}
                          setQuoteValue={setQuoteValue}
                          disabled={!isQuoteEdit}
                        />
                      </Grid>
                      <Grid item lg={6} md={12} sm={12} xs={12} style={{ margin: "0 0 14px" }}>
                        <Deposit
                          applicationId={applicationId}
                          fieldErrors={fieldErrors}
                          setFieldErrors={setFieldErrors}
                          quoteValues={quoteValues}
                          setQuoteValue={setQuoteValue}
                          disabled={!isQuoteEdit}
                        />
                      </Grid>
                    </Grid>
                    <Grid container item direction="row" spacing={1}>
                      <Grid item lg={6} md={12} sm={12} xs={12} style={{ margin: "0 0 14px" }}>
                        <AssetCondition applicationId={applicationId} disabled={!isQuoteEdit} />
                      </Grid>
                      <Grid item lg={6} md={12} sm={12} xs={12} style={{ margin: "0 0 14px" }}>
                        <AgeOfAsset applicationId={applicationId} disabled={!isQuoteEdit} />
                      </Grid>
                    </Grid>

                    <Grid item md={12} sm={12} style={formFieldsStyle}>
                      <AssetType
                        applicationId={applicationId}
                        applicationType={application.applicationType}
                        disabled={!isQuoteEdit}
                      />
                    </Grid>
                    <Grid item md={12} sm={12} style={formFieldsStyle}>
                      <TypeOfSale applicationId={applicationId} disabled={!isQuoteEdit} />
                    </Grid>
                  </>
                )}

                <Grid container item direction="row" spacing={1}>
                  <Grid item lg={6} md={12} sm={12} xs={12} style={formFieldsStyle}>
                    <Term disabled={!isQuoteEdit} />
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12} style={formFieldsStyle}>
                    <OriginationFee />
                  </Grid>
                </Grid>

                {isAssetLoan && (
                  <Grid item md={12} sm={12} xs={12} style={formFieldsStyle}>
                    <Balloon applicationId={applicationId} disabled={!isQuoteEdit} />
                  </Grid>
                )}
                {isCommercialOrBusinessApplication && (
                  <Grid item md={12} sm={12} xs={12} style={formFieldsStyle}>
                    <Brokerage applicationId={applicationId} />
                  </Grid>
                )}
                {isConsumer && (
                  <Grid item md={12} sm={12} xs={12} style={formFieldsStyle}>
                    <RateAdjustment applicationId={applicationId} />
                  </Grid>
                )}

                <Grid container item direction="row" spacing={1}>
                  <Grid item md={12} sm={12} xs={12} style={formFieldsStyle}>
                    <LivingArrangements applicationId={applicationId} disabled={!isQuoteEdit} />
                  </Grid>
                </Grid>
                <Grid container item direction="row" spacing={1} style={{ marginBottom: "14px" }}>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <QuoteDateOfBirth applicationId={applicationId} disabled={!isQuoteEdit} />
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <QuoteCreditScore applicationId={applicationId} disabled={!isQuoteEdit} />
                  </Grid>
                </Grid>

                <Grid container item direction="row" spacing={1} style={{ marginBottom: "14px" }}>
                  <Grid item md={12} sm={12} xs={12}>
                    <Email
                      customerId={customer?._id}
                      fieldErrors={fieldErrors}
                      setFieldErrors={setFieldErrors}
                      email={customer ? findEmail || customer?.emails?.slice(-1)[0] : ""}
                      applicationId={applicationId}
                      disabled={!isQuoteEdit}
                    />
                  </Grid>
                </Grid>

                {isConsumerOrPersonalApplication && (
                  <Grid sm={12} item style={formFieldsStyle}>
                    <EmploymentType applicationId={applicationId} disabled={!isQuoteEdit} />
                  </Grid>
                )}

                {isConsumerOrPersonalApplication && (
                  <Grid sm={12} item style={{ margin: "0 0 10px 0" }}>
                    <LengthOfEmployment applicationId={applicationId} disabled={!isQuoteEdit} />
                  </Grid>
                )}

                {isCommercialOrBusinessApplication && (
                  <Grid sm={12} item style={formFieldsStyle}>
                    <QuoteEntity application={application} disabled={!isQuoteEdit} />
                  </Grid>
                )}

                <Grid
                  sm={12}
                  item
                  style={{
                    margin: "0 0 10px 0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "10px",
                  }}
                >
                  <Tooltip title={sendQuoteTooltip} placement="top">
                    {/* span wrapper lets the tooltip show while the button is disabled */}
                    <span>
                      <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        onClick={handle.sendQuoteHandler}
                        disabled={isSendQuoteDisabled}
                      >
                        {sendQuoteLoader ? "Sending..." : "Send Quote"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={handle.startApplicationHandler}
                    disabled={!(permissions?.application?.quote_convert && isQuoteEdit) || startApplicationBtnLoader}
                  >
                    {startApplicationBtnLoader ? "Starting..." : "Start Application"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid
            conatiner
            item
            sm={8}
            md={9}
            lg={9}
            style={{
              padding: 0,
            }}
          >
            <Box
              style={{
                background: theme.palette.neutral[100],

                padding: "10px 20px",
                position: "relative",
                scrollbarColor: "white rebeccapurple",
                scrollbarWidth: "thin",
              }}
            >
              {lenderData ? (
                <ProductSelector color="secondary" lenderData={lenderData} />
              ) : (
                <PageLoader text={"Lenders Loading"} />
              )}
            </Box>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default QuoteCondensed;
